import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Building2, DoorOpen, Eye, Receipt, Trash2, Users } from 'lucide-react';
import { adminQueries, getErrorMessage, hasProblemType, keys } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatNumber, hasPermission } from '@blimo/shared';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  ConfirmDialog,
  ErrorState,
  PageHeader,
  StatCard,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@blimo/ui';
import { DetailSkeleton, PartyLink, SourceBadge } from '../../components/data.jsx';
import { MembersTab } from './members.jsx';
import { PropertyTree } from './property-tree.jsx';
import { ReportsTab } from './reports.jsx';
import { MANDATE_TABS } from './utils.js';

const n = (/** @type {number|undefined} */ v) => formatNumber(v ?? 0, { maximumFractionDigits: 0 });

/** Mandant – Detail mit Tabs Einheiten / Kostenkonten / Mitglieder. Ändern nur mit `mandates.manage`, sonst lesend. */
export function MandateDetailPage() {
  const api = useApi();
  const { data: me } = useMe();
  const { clientId } = useParams({ strict: false });
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [removeOpen, setRemoveOpen] = useState(false);
  const { data: client, error, isPending, refetch } = useQuery(adminQueries.mandate(api, clientId));
  const canEdit = hasPermission(me, 'mandates.manage');

  const tab = MANDATE_TABS.some((t) => t.id === search.tab) ? search.tab : 'einheiten';
  const setTab = (next) =>
    navigate({ to: '/finanzen/mandanten/$clientId', params: { clientId }, search: { tab: next === 'einheiten' ? undefined : next }, replace: true });

  const breadcrumb = (
    <Link to="/finanzen/mandanten" className="hover:underline">
      Mandanten & Freigaben
    </Link>
  );

  if (error) {
    return (
      <>
        <PageHeader title="Mandant" breadcrumb={breadcrumb} />
        <ErrorState
          error={error}
          onRetry={() => refetch()}
          notFoundTitle="Mandant nicht gefunden"
          action={
            <Button asChild variant="outline" size="sm">
              <Link to="/finanzen/mandanten">Zur Liste</Link>
            </Button>
          }
        />
      </>
    );
  }
  if (isPending) return <DetailSkeleton />;

  const counts = client.counts ?? {};
  const unused = !counts.units && !counts.cost_accounts && !counts.members;

  async function removeFlag() {
    try {
      await api.admin.mandates.remove(client.id);
      await queryClient.invalidateQueries({ queryKey: keys.admin.resource('mandates') });
      toast.success('Mandanten-Kennzeichen entfernt');
      navigate({ to: '/finanzen/mandanten' });
    } catch (e) {
      toast.error(
        hasProblemType(e, 'client-in-use')
          ? 'Der Mandant hat noch Einheiten, Kostenkonten oder Mitglieder.'
          : getErrorMessage(e, 'Das Kennzeichen konnte nicht entfernt werden.'),
      );
    }
  }

  return (
    <>
      <PageHeader
        breadcrumb={breadcrumb}
        title={client.name || `Mandant #${client.id}`}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <PartyLink party={{ id: client.id, display_name: 'Organisation öffnen' }} />
            <SourceBadge source={client.source} />
            {client.archived_at && <Badge variant="neutral">Archiviert</Badge>}
            {!canEdit && (
              <Badge variant="outline">
                <Eye aria-hidden="true" className="size-3" /> Nur Ansicht
              </Badge>
            )}
          </span>
        }
        actions={
          canEdit &&
          unused && (
            <Button size="sm" variant="outline" onClick={() => setRemoveOpen(true)}>
              <Trash2 aria-hidden="true" /> Kennzeichen entfernen
            </Button>
          )
        }
      />
      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4" role="region" aria-label="Kennzahlen">
        <StatCard label="Einheiten" icon={DoorOpen} value={n(counts.units)} />
        <StatCard label="Liegenschaften" icon={Building2} value={n(counts.properties)} />
        <StatCard label="Freigegebene Kostenkonten" icon={Receipt} value={n(counts.cost_accounts)} />
        <StatCard label="Mitglieder" icon={Users} value={n(counts.members)} />
      </div>
      {counts.units > 0 && !counts.members && (
        <Alert variant="warning" className="mb-3">
          <AlertDescription>Diesem Mandanten sind Einheiten zugeordnet, aber noch keine Mitglieder – im Kundenportal sieht sie niemand.</AlertDescription>
        </Alert>
      )}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList aria-label="Bereiche des Mandanten">
          {MANDATE_TABS.map((t) => (
            <TabsTrigger key={t.id} value={t.id}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="einheiten">
          <p className="mb-3 text-sm text-muted-foreground">
            Zugeordnete Einheiten und deren Liegenschaften sind für die Mitglieder im Kundenportal sichtbar (Mieten, Rendite, offene Posten).
          </p>
          <PropertyTree clientId={String(client.id)} mode="units" canEdit={canEdit} />
        </TabsContent>
        <TabsContent value="kostenkonten">
          <p className="mb-3 text-sm text-muted-foreground">Im Portal erscheinen nur Kostenbuchungen der hier freigegebenen Sachkonten – je Liegenschaft.</p>
          <PropertyTree clientId={String(client.id)} mode="accounts" canEdit={canEdit} />
        </TabsContent>
        <TabsContent value="mitglieder">
          <MembersTab client={client} canEdit={canEdit} />
        </TabsContent>
        <TabsContent value="berichte">
          <ReportsTab client={client} canEdit={canEdit} />
        </TabsContent>
      </Tabs>
      {canEdit && (
        <ConfirmDialog
          open={removeOpen}
          onOpenChange={setRemoveOpen}
          title="Mandanten-Kennzeichen entfernen?"
          description="Die Organisation bleibt erhalten, erscheint aber nicht mehr unter „Mandanten & Freigaben“."
          confirmLabel="Entfernen"
          destructive
          onConfirm={removeFlag}
        />
      )}
    </>
  );
}
